import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import Card from '../components/Structure/Card.tsx';
import TextInput from '../components/Form/TextInput.tsx';
import Select from '../components/Form/Select.tsx';
import Datepicker from '../components/Form/Datepicker.tsx';
import Button from '../components/Form/Button.tsx';
import PasswordInputWithValidation from '../components/Form/PasswordInputWithValidation.tsx';
import { useAuthlessApi } from '../features/auth/hooks/use-authless-api.ts';
import { ApiResponseType } from '../features/api/types.ts';
import { iso3166 } from '../static/iso3166.js';
import { IOption } from '../interface/IOption';
import { useValidators } from '../features/functions/validators.tsx';
import { useHelper } from '../features/functions/helper.tsx';
import { useSpamProtection } from '../features/functions/spamProtection.tsx';
import '../scss/components/Registration.scss';

const salutationOptions = [
    { value: '2', label: 'divers' },
    { value: '1', label: 'Frau' },
    { value: '0', label: 'Herr' },
];

type PreferredContactOption = {
    value: string,
    label: string,
    keys: string[],
    format?: (data:any) => string,
};

type PreferredContactGroup = {
    field: string,
    heading: string,
    options: PreferredContactOption[],
};

const formatAddress = (prefix:string) => (data:any) => {
    const country = iso3166.find((entry:any) => entry.value === data[`${prefix}_country`]);
    return `${data[`${prefix}_address`]}, ${data[`${prefix}_zip`]} ${data[`${prefix}_city`]}${country ? `, ${country.label}` : ''}`;
};

// `keys` are the form fields an option needs — an option whose fields are empty
// has nothing to select and is not offered.
const preferredContactGroups:PreferredContactGroup[] = [
    {
        field: 'preferred_contact_email_source',
        heading: 'Über welche E-Mail-Adresse soll der FPSB Deutschland bevorzugt mit Ihnen kommunizieren?',
        options: [
            { value: 'private_email', label: 'Private E-Mail-Adresse', keys: ['private_email'] },
            { value: 'company_email', label: 'Geschäftliche E-Mail-Adresse', keys: ['company_email'] },
        ],
    },
    {
        field: 'preferred_contact_phone_source',
        heading: 'Über welche Telefonnummer soll der FPSB Deutschland bevorzugt mit Ihnen kommunizieren?',
        options: [
            { value: 'private_mobile', label: 'Private Mobilnummer', keys: ['private_mobile'] },
            { value: 'private_phone', label: 'Private Festnetznummer', keys: ['private_phone'] },
            { value: 'company_mobile', label: 'Geschäftliche Mobilnummer', keys: ['company_mobile'] },
            { value: 'company_phone', label: 'Geschäftliche Festnetznummer', keys: ['company_phone'] },
        ],
    },
    {
        field: 'preferred_contact_address_source',
        heading: 'Über welche Postadresse soll der FPSB Deutschland bevorzugt mit Ihnen kommunizieren?',
        options: [
            { value: 'private', label: 'Private Adresse', keys: ['private_address', 'private_zip', 'private_city'], format: formatAddress('private') },
            { value: 'company', label: 'Geschäftliche Adresse', keys: ['company_address', 'company_zip', 'company_city'], format: formatAddress('company') },
        ],
    },
];

/** The line shown for an option, empty when one of its fields has no value. */
const optionDisplay = (option:PreferredContactOption, data:any) => {
    if (option.keys.some((key:string) => !String(data[key] ?? '').trim())) {
        return '';
    }
    return option.format ? option.format(data) : String(data[option.keys[0]]).trim();
};

/** Groups reduced to their selectable options; groups without any option are left out. */
const visiblePreferredContactGroups = (data:any) => preferredContactGroups
    .map((group) => ({
        ...group,
        options: group.options.filter((option) => optionDisplay(option, data) !== ''),
    }))
    .filter((group) => group.options.length > 0);

/**
 * Keeps the preferred-contact selections in line with the options on offer:
 * a selection whose option lost its value is dropped, so nothing hidden is submitted;
 * a group with a single option has it selected; and a group that just grew from
 * one option to several is cleared, so the user has to choose deliberately.
 */
const syncPreferences = (prev:any, data:any) => {
    const next = { ...data };
    const prevCounts = Object.fromEntries(
        visiblePreferredContactGroups(prev).map(({ field, options }) => [field, options.length])
    );
    preferredContactGroups.forEach(({ field, options }) => {
        const selected = options.find((option) => option.value === next[field]);
        if (next[field] && (!selected || !optionDisplay(selected, next))) {
            next[field] = '';
        }
    });
    visiblePreferredContactGroups(next).forEach(({ field, options }) => {
        if (options.length === 1) {
            next[field] = options[0].value;
        } else if (prevCounts[field] === 1) {
            next[field] = '';
        }
    });
    return next;
};

const privacyUrl = process.env.REACT_APP_FPSB_PORTAL_PRIVACY_URL;
const dsgvoText = `<p>Die von Ihnen im Formular eingegebenen Daten werden zur Bearbeitung Ihrer Anfrage auf Grundlage von <span style="white-space: nowrap;">Art. 6 Abs. 1 lit. b DSGVO</span> (vorvertragliche Maßnahmen) verarbeitet. Mit dem Absenden des Formulars bestätigen Sie, dass Sie die Datenschutzhinweise zur Kenntnis genommen haben.${privacyUrl ? ` Weitere Informationen zum Umgang mit Ihren Daten finden Sie in unserer <a href="${privacyUrl}" target="_blank">Datenschutzerklärung</a>.` : ''}</p>`;

const initialFormData = {
    salutation: '',
    title: '',
    firstname: '',
    lastname: '',
    birth_name: '',
    name_addition: '',
    contact_date_of_birth: null,
    private_address: '',
    private_zip: '',
    private_city: '',
    private_country: '',
    private_phone: '',
    private_mobile: '',
    private_email: '',
    company_company: '',
    company_job_status: '',
    branch_sectors: '',
    company_department: '',
    company_address: '',
    company_zip: '',
    company_city: '',
    company_country: '',
    company_phone: '',
    company_mobile: '',
    company_email: '',
    preferred_contact_email_source: '',
    preferred_contact_phone_source: '',
    preferred_contact_address_source: '',
};

export default function Registration() {
    const [formData, setFormData] = useState<any>(initialFormData);
    const [password, setPassword] = useState('');
    const [passwordValid, setPasswordValid] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseMessageClass, setResponseMessageClass] = useState('');
    const [nonce, setNonce] = useState('');
    const [titleOptions, setTitleOptions] = useState<Array<IOption>>([]);
    const [jobStatusOptions, setJobStatusOptions] = useState<Array<IOption>>([]);
    const [branchSectorOptions, setBranchSectorOptions] = useState<Array<IOption>>([]);

    const { register, requestRegisterNonce, getRegistrationLists } = useAuthlessApi();
    const { validatePhone } = useValidators();
    const { checkProfileFieldsForErrors } = useHelper();
    const {
        honeypot,
        setHoneypot,
        isLikelyBot,
        isRateLimited,
        remainingRateLimitSeconds,
        markSubmitted,
        getSpamSignals,
    } = useSpamProtection({ rateLimitKey: 'fpsb.portal.registration.lastSubmit' });

    const extractNonce = (data:any) => {
        const p:any = data?.payload;
        return (p && p.nonce) ? p.nonce : '';
    };

    const fetchNonce = () => {
        requestRegisterNonce().then(data => {
            setNonce(extractNonce(data));
        }).catch(() => {});
    };

    // The dropdowns are backed by CRM lists (title: `Anrede Zusätze`, company_job_status:
    // `Beruflicher Status`, branch_sectors: `Branchenbereiche`). The option value must be
    // the CRM entry id: the backend maps it through ListService, which only accepts an id.
    const fetchListOptions = () => {
        getRegistrationLists().then((data:any) => {
            const payload:any = data?.payload;
            const toOptions = (listName:string):Array<IOption>|null => {
                const entries = payload?.[listName]?.entries;
                if (!Array.isArray(entries)) {
                    return null;
                }
                return [...entries]
                    .sort((a:any, b:any) => a.orderNumber - b.orderNumber)
                    .map((entry:any) => ({ value: String(entry.id), label: entry.label }));
            };
            const title = toOptions('title');
            const jobStatus = toOptions('company_job_status');
            const branchSectors = toOptions('branch_sectors');
            if (title) setTitleOptions(title);
            if (jobStatus) setJobStatusOptions(jobStatus);
            if (branchSectors) setBranchSectorOptions(branchSectors);
        }).catch(() => {});
    };

    useEffect(() => {
        fetchNonce();
        fetchListOptions();
    }, []);

    const handleChange = (event:any, name = '', value:any = null) => {
        const key = name ? name : event.target.name;
        setFormData(syncPreferences(formData, { ...formData, [key]: value !== null ? value : event.target.value }));
    };

    const handleSelectChange = (name:string, value:string) => {
        setFormData(syncPreferences(formData, { ...formData, [name]: value }));
    };

    const handlePasswordChange = (pwFormData:any, inputPasswordCheckPassed:boolean) => {
        setPassword(pwFormData.newpassword);
        setPasswordValid(inputPasswordCheckPassed);
    };

    const contactOption = (field:string, option:PreferredContactOption) => {
        const display = optionDisplay(option, formData);
        if (!display) {
            return null;
        }
        const isSelected = formData[field] === option.value;

        return (
            <div className='contact-item-box' key={`${field}-${option.value}`}>
                <Button
                    buttonType='small'
                    type='button'
                    className={isSelected ? 'outline' : ''}
                    onClick={(e:any) => {
                        e.preventDefault();
                        handleSelectChange(field, option.value);
                        if (errors[field]) {
                            setErrors({ ...errors, [field]: undefined });
                        }
                    }}
                >{isSelected ? 'gewählt' : 'auswählen'}</Button>
                <p>{option.label}:</p>
                <p>{display}</p>
            </div>
        );
    };

    const validate = () => {
        let validationErrors:any = {};

        // Mandatory fields: personal details, home address & business address
        const requiredFields = {
            firstname: 'text',
            lastname: 'text',
            private_address: 'text',
            private_zip: 'text',
            private_city: 'text',
            private_country: 'text',
            private_email: 'email',
            company_company: 'text',
            company_address: 'text',
            company_zip: 'text',
            company_city: 'text',
            company_country: 'text',
            company_email: 'email',
        };
        validationErrors = checkProfileFieldsForErrors(formData, requiredFields);

        // Either private phone or mobile is required
        if (!formData.private_phone && !formData.private_mobile) {
            validationErrors.private_phone = 'Festnetznummer oder Mobilnummer muss angegeben werden';
            validationErrors.private_mobile = 'Festnetznummer oder Mobilnummer muss angegeben werden';
        }
        // Validate format only when a value is present
        if (formData.private_phone) {
            const phoneValid = validatePhone(formData.private_phone, false, 'Festnetznummer');
            if (phoneValid !== true) {
                validationErrors.private_phone = phoneValid;
            }
        }
        if (formData.private_mobile) {
            const mobileValid = validatePhone(formData.private_mobile, false, 'Mobilnummer');
            if (mobileValid !== true) {
                validationErrors.private_mobile = mobileValid;
            }
        }

        // Either business phone or mobile is required (same rule as private)
        if (!formData.company_phone && !formData.company_mobile) {
            validationErrors.company_phone = 'Festnetznummer oder Mobilnummer muss angegeben werden';
            validationErrors.company_mobile = 'Festnetznummer oder Mobilnummer muss angegeben werden';
        }
        if (formData.company_phone) {
            const phoneValid = validatePhone(formData.company_phone, false, 'Festnetznummer');
            if (phoneValid !== true) {
                validationErrors.company_phone = phoneValid;
            }
        }
        if (formData.company_mobile) {
            const mobileValid = validatePhone(formData.company_mobile, false, 'Mobilnummer');
            if (mobileValid !== true) {
                validationErrors.company_mobile = mobileValid;
            }
        }

        // A preferred contact must be chosen wherever more than one option is offered.
        // The API cannot check this, since it does not know which options were shown.
        visiblePreferredContactGroups(formData).forEach(({ field, options }) => {
            if (options.length > 1 && !formData[field]) {
                validationErrors[field] = 'Bitte wählen Sie eine bevorzugte Option aus';
            }
        });

        if (!formData.salutation) {
            validationErrors.salutation = 'Bitte wählen Sie eine Anrede';
        }

        if (!formData.contact_date_of_birth) {
            validationErrors.contact_date_of_birth = 'Bitte geben Sie Ihr Geburtsdatum an';
        }

        if (!password || !passwordValid) {
            validationErrors.password = 'Bitte vergeben Sie ein gültiges Passwort gemäß den Hinweisen (übereinstimmend).';
        }

        return validationErrors;
    };

    const handleSubmit = async (e:any) => {
        e.preventDefault();
        setResponseMessage('');

        // Silent bot detection (honeypot/timing): Fake success without an API call.
        if (isLikelyBot()) {
            setSubmitted(true);
            return;
        }

        // Client-side rate limit to prevent rapid resubmissions.
        if (isRateLimited()) {
            setResponseMessage(`Bitte warten Sie ${remainingRateLimitSeconds()} Sekunden, bevor Sie das Formular erneut absenden.`);
            setResponseMessageClass('error');
            return;
        }

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setResponseMessage('Bitte korrigieren Sie die markierten Felder.');
            setResponseMessageClass('error');
            return;
        }

        setErrors({});
        setIsLoading(true);

        const payload = {
            ...formData,
            // Unix seconds, as the profile stores it (negative before 1970).
            contact_date_of_birth: Math.floor((formData.contact_date_of_birth as Date).getTime() / 1000),
            password: password,
            nonce: nonce,
            ...getSpamSignals(),
        };

        register(payload).then(data => {
            setIsLoading(false);
            const payloadObj:any = data.payload;
            if (data.type === ApiResponseType.OK && (!payloadObj || payloadObj.success !== false)) {
                markSubmitted();
                setSubmitted(true);
            } else {
                fetchNonce();
                const apiErrors = payloadObj && payloadObj.errors ? payloadObj.errors : null;
                if (apiErrors) {
                    setErrors(apiErrors);
                    if (!apiErrors.general_error) {
                        setResponseMessage('Bitte korrigieren Sie die markierten Felder.');
                        setResponseMessageClass('error');
                    } else {
                        setResponseMessage(apiErrors.general_error);
                        setResponseMessageClass('error');
                    }
                } else {
                    setResponseMessage((payloadObj && payloadObj.error) ? payloadObj.error : 'Die Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es später erneut.');
                    setResponseMessageClass('error');
                }
            }
        }).catch(() => {
            setIsLoading(false);
            fetchNonce();
            setResponseMessage('Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.');
            setResponseMessageClass('error');
        });
    };

    if (submitted) {
        return (
            <Card p={'var(--spacing-2xl)'} gap={'var(--spacing-xl)'}>
                <h1 className="h3 align-center">Vielen Dank für Ihre Registrierung</h1>
                <p className="m-0 align-center">Wir haben Ihnen eine E-Mail mit einem Bestätigungslink geschickt. Bitte klicken Sie auf den Link in der E-Mail, um Ihre Registrierung abzuschließen. Bitte kontrollieren Sie auch Ihren Spam-Ordner.</p>
                <p className="m-0 text-xs align-center">
                    <Link to={`/profile`}>
                        <Button fullWidth buttonType='primary'>zurück zur Anmeldung</Button>
                    </Link>
                </p>
            </Card>
        );


    }

    const visiblePreferredGroups = visiblePreferredContactGroups(formData);

    return (
        <Card className='registration-card' p={'var(--spacing-2xl)'} gap={'var(--spacing-xl)'}>
            <h1 className="h3 align-center">Registrierung</h1>
            <form onSubmit={handleSubmit} className={`registration-form${isLoading ? ' loading' : ''}`}>
                <div className="loader"></div>
                <span>Die mit Sternchen gekennzeichneten Felder sind Pflichtfelder.</span>

                <h2 className="h5">Persönliche Informationen</h2>
                <div className="row">
                    <Select
                        wrapperClassName='col-6'
                        isSearchable={false}
                        isClearable={false}
                        label='Anrede'
                        placeholder='Anrede auswählen'
                        name='salutation'
                        onChange={(event:any) => handleSelectChange('salutation', event.value)}
                        defaultValue={formData.salutation}
                        options={salutationOptions}
                        error={errors['salutation']}
                        isRequired={true}
                    />
                    <Select
                        wrapperClassName='col-6'
                        isSearchable={false}
                        isClearable={true}
                        label='akademischer Titel'
                        placeholder='kein Titel'
                        name='title'
                        onChange={(event:any, action:any) => {
                            if (event) {
                                handleSelectChange('title', event.value);
                            }
                            if (!event && action.action === 'clear') {
                                handleSelectChange('title', '');
                            }
                        }}
                        defaultValue={formData.title}
                        options={titleOptions}
                        error={errors['title']}
                    />
                </div>
                <div className="row">
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.firstname}
                        label='Vorname & Mittelname'
                        name='firstname'
                        required={true}
                        error={errors['firstname']}
                    />
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.lastname}
                        label='Nachname'
                        name='lastname'
                        required={true}
                        error={errors['lastname']}
                    />
                </div>
                <div className="row">
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.birth_name}
                        label='Geburtsname'
                        name='birth_name'
                        error={errors['birth_name']}
                    />
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.name_addition}
                        label='Namenszusatz'
                        name='name_addition'
                        error={errors['name_addition']}
                    />
                </div>
                <Datepicker
                    selected={formData.contact_date_of_birth}
                    onChange={(date:Date|null) => {
                        setFormData(syncPreferences(formData, { ...formData, contact_date_of_birth: date }));
                    }}
                    locale='de'
                    dateFormat='dd.MM.yyyy'
                    showYearDropdown={true}
                    showMonthDropdown={true}
                    minDate={new Date(1900, 0, 1)}
                    maxDate={new Date()}
                    label='Geburtsdatum'
                    isRequired={true}
                    name='contact_date_of_birth'
                    error={errors['contact_date_of_birth']}
                />
                <PasswordInputWithValidation
                    outerChangeHandler={handlePasswordChange}
                    label='Passwort'
                    error={errors['password']}
                    spacing={2}
                />

                <h2 className="h5">Private Adresse</h2>
                <TextInput
                    onChange={handleChange}
                    value={formData.private_address}
                    label='Straße, Hausnummer'
                    name='private_address'
                    required={true}
                    error={errors['private_address']}
                />
                <div className="row">
                    <TextInput
                        wrapperClassName='col-3'
                        onChange={handleChange}
                        value={formData.private_zip}
                        label='PLZ'
                        name='private_zip'
                        required={true}
                        error={errors['private_zip']}
                    />
                    <TextInput
                        wrapperClassName='col-5'
                        onChange={handleChange}
                        value={formData.private_city}
                        label='Ort'
                        name='private_city'
                        required={true}
                        error={errors['private_city']}
                    />
                    <Select
                        wrapperClassName='col-4'
                        isSearchable={true}
                        isClearable={false}
                        label='Land'
                        placeholder='Land auswählen'
                        name='private_country'
                        onChange={(event:any) => handleSelectChange('private_country', event.value)}
                        defaultValue={formData.private_country}
                        options={iso3166}
                        error={errors['private_country']}
                        isRequired={true}
                    />
                </div>
                <div className="row">
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.private_phone}
                        label='Festnetz'
                        name='private_phone'
                        type='tel'
                        required={formData.private_mobile === ''}
                        error={errors['private_phone']}
                        placeholder="+49123456789123"
                    />
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.private_mobile}
                        label='Mobil'
                        name='private_mobile'
                        type='tel'
                        required={formData.private_phone === ''}
                        error={errors['private_mobile']}
                        placeholder="+49123456789123"
                    />
                </div>
                <TextInput
                    onChange={handleChange}
                    value={formData.private_email}
                    label='E-Mail'
                    name='private_email'
                    type='email'
                    required={true}
                    error={errors['private_email']}
                    placeholder="max.mustermann@beispiel.de"
                />

                <h2 className="h5">Geschäftliche Adresse</h2>
                <div className="row">
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.company_company}
                        label='Unternehmen/Arbeitgeber'
                        name='company_company'
                        required={true}
                        error={errors['company_company']}
                    />
                    <Select
                        wrapperClassName='col-6'
                        isSearchable={false}
                        isClearable={true}
                        label='Beruflicher Status'
                        placeholder='Status auswählen'
                        name='company_job_status'
                        onChange={(event:any, action:any) => {
                            if (event) {
                                handleSelectChange('company_job_status', event.value);
                            }
                            if (!event && action.action === 'clear') {
                                handleSelectChange('company_job_status', '');
                            }
                        }}
                        defaultValue={formData.company_job_status}
                        options={jobStatusOptions}
                        error={errors['company_job_status']}
                    />
                </div>
                <Select
                    isSearchable={true}
                    isClearable={true}
                    label='Branche'
                    placeholder='Branche auswählen'
                    name='branch_sectors'
                    onChange={(event:any, action:any) => {
                        if (event) {
                            handleSelectChange('branch_sectors', event.value);
                        }
                        if (!event && action.action === 'clear') {
                            handleSelectChange('branch_sectors', '');
                        }
                    }}
                    defaultValue={formData.branch_sectors}
                    options={branchSectorOptions}
                    error={errors['branch_sectors']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData.company_department}
                    label='Abteilung'
                    name='company_department'
                    error={errors['company_department']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData.company_address}
                    label='Straße, Hausnummer'
                    name='company_address'
                    required={true}
                    error={errors['company_address']}
                />
                <div className="row">
                    <TextInput
                        wrapperClassName='col-3'
                        onChange={handleChange}
                        value={formData.company_zip}
                        label='PLZ'
                        name='company_zip'
                        required={true}
                        error={errors['company_zip']}
                    />
                    <TextInput
                        wrapperClassName='col-5'
                        onChange={handleChange}
                        value={formData.company_city}
                        label='Ort'
                        name='company_city'
                        required={true}
                        error={errors['company_city']}
                    />
                    <Select
                        wrapperClassName='col-4'
                        isSearchable={true}
                        isClearable={false}
                        label='Land'
                        placeholder='Land auswählen'
                        name='company_country'
                        onChange={(event:any) => handleSelectChange('company_country', event.value)}
                        defaultValue={formData.company_country}
                        options={iso3166}
                        error={errors['company_country']}
                        isRequired={true}
                    />
                </div>
                <div className="row">
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.company_phone}
                        label='Festnetz'
                        name='company_phone'
                        type='tel'
                        required={formData.company_mobile === ''}
                        error={errors['company_phone']}
                        placeholder="+49123456789123"
                    />
                    <TextInput
                        wrapperClassName='col-6'
                        onChange={handleChange}
                        value={formData.company_mobile}
                        label='Mobil'
                        name='company_mobile'
                        type='tel'
                        required={formData.company_phone === ''}
                        error={errors['company_mobile']}
                        placeholder="+49123456789123"
                    />
                </div>
                <TextInput
                    onChange={handleChange}
                    value={formData.company_email}
                    label='E-Mail'
                    name='company_email'
                    type='email'
                    required={true}
                    error={errors['company_email']}
                    placeholder="max.mustermann@beispiel.de"
                />

                { visiblePreferredGroups.length > 0 && (
                    <>
                        <h2 className="h5">Bevorzugte Kontaktdaten</h2>
                        <div className="preferred-contact">
                            { visiblePreferredGroups.map((group, index) => (
                                <React.Fragment key={group.field}>
                                    <h6 className={index === 0 ? 'mt-0' : ''}>{group.heading} {group.options.length > 1 ? '*' : ''}</h6>
                                    <div className="box-wrapper">
                                        { group.options.map((option) => contactOption(group.field, option)) }
                                    </div>
                                    { errors[group.field] && (
                                        <span className='form-error' role='alert'>{ errors[group.field] }</span>
                                    ) }
                                    { visiblePreferredGroups.length - 1 !== index && <hr /> }
                                </React.Fragment>
                            )) }
                        </div>
                    </>
                ) }

                <div className="registration-dsgvo" dangerouslySetInnerHTML={{ __html: dsgvoText }}></div>

                <div className="registration-hp" aria-hidden="true">
                    <label htmlFor="website">Website (bitte freilassen)</label>
                    <input
                        type="text"
                        id="website"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                    />
                </div>

                { responseMessage && responseMessage.length > 0 && (
                    <div className={`message${responseMessageClass ? ' ' + responseMessageClass : ''}`} role="alert">
                        <span>{responseMessage}</span>
                    </div>
                )}

                <Button
                    fullWidth
                    buttonType='primary'
                    onClick={handleSubmit}
                >Registrieren</Button>
                <p className="m-0 text-xs align-center">bereits registriert? <Link className="text-xs" to={`/profile`}>zur Anmeldung</Link></p>
            </form>
        </Card>
    );
}
